import React from 'react';
import ContestInvoice from './ContestInvoice';

const KIND_TEXT = {
  received: 'against what was delivered',
  ordered: 'against what was ordered',
  price: 'against the agreed price',
};

const money = (v) => (v === null || v === undefined ? null : `£${v}`);

/**
 * Where this invoice disagrees with the delivery, the order or the agreed price (backend/invoice_discrepancy.py): the
 * facts a dispute with the supplier starts from. Only managers see it, as it carries prices. A line the supplier billed
 * LESS on is shown but never called contestable. Contesting from here comes next
 * (docs/architecture/INVOICE-DISPUTES-2026-11-04.md); for now this says what to take up with the supplier.
 */
export default function InvoiceDiscrepancy({ discrepancy, canDecide = true, invoice, onChanged }) {
  const lines = discrepancy?.lines || [];
  if (!canDecide || !lines.length) return null;
  const waiting = invoice && ['needs_review', 'awaiting_approval'].includes(invoice.state);
  const contestable = discrepancy.contestable && waiting && invoice.dispute?.state !== 'open';
  const extra = money(discrepancy.contestable_amount);
  return (
    <section className="rounded-2xl border border-amber-300 bg-amber-50 p-5 space-y-3" data-testid="invoice-discrepancy">
      <h2 className="text-lg font-semibold text-gray-900">Does not match what we ordered or received</h2>
      <p className="text-sm text-gray-700" data-testid="invoice-discrepancy-summary">
        {discrepancy.contestable
          ? `The supplier has billed more than it should have${extra ? `, about ${extra} too much` : ''}. Take it up with them before approving.`
          : 'The differences below are in our favour or need no action.'}
      </p>
      <ul className="space-y-2">
        {lines.map((ln, i) => (
          <li key={`${ln.kind}-${ln.item_id || ln.description}-${i}`} className="text-sm border-b border-amber-200 pb-2"
              data-testid={`invoice-discrepancy-line-${i}`}>
            <span className="font-medium">{ln.description || ln.item_id || 'Line'}</span>
            {' — '}
            billed {ln.billed}, {ln.kind === 'price' ? 'agreed' : 'expected'} {ln.expected}{ln.unit && ln.kind !== 'price' ? ` ${ln.unit}` : ''}
            {' '}({KIND_TEXT[ln.kind] || ln.kind})
            {ln.contestable && ln.amount ? `, £${ln.amount} too much` : ''}
          </li>
        ))}
      </ul>
      {contestable && <ContestInvoice invoice={invoice} onChanged={onChanged} />}
    </section>
  );
}
